const API_URL = process.env.REACT_APP_BACKEND_URL;

export const api = {
  // Admin Auth
  login: async (email, password) => {
    const response = await fetch(`${API_URL}/api/admin/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Login failed');
    }
    return response.json();
  },

  forgotPassword: async (email) => {
    const response = await fetch(`${API_URL}/api/admin/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Request failed');
    }
    return response.json();
  },

  resetPassword: async (token, newPassword) => {
    const response = await fetch(`${API_URL}/api/admin/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, new_password: newPassword }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Reset failed');
    }
    return response.json();
  },

  // Admin Profile
  getProfile: async (token) => {
    const response = await fetch(`${API_URL}/api/admin/profile`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch profile');
    }
    return response.json();
  },

  updateProfile: async (token, data) => {
    const response = await fetch(`${API_URL}/api/admin/profile`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Update failed');
    }
    return response.json();
  },

  updatePassword: async (token, currentPassword, newPassword) => {
    const response = await fetch(`${API_URL}/api/admin/profile/password`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Password update failed');
    }
    return response.json();
  },

  // Dashboard
  getDashboardStats: async (token) => {
    const response = await fetch(`${API_URL}/api/admin/dashboard/stats`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch stats');
    }
    return response.json();
  },

  // Customers
  getCustomers: async (token, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.status) queryParams.append('status', params.status);
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);

    const response = await fetch(
      `${API_URL}/api/admin/customers?${queryParams.toString()}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch customers');
    }
    return response.json();
  },

  getCustomerDetail: async (token, customerId) => {
    // Allow synthetic IDs like "invoice:<uuid>" to pass through correctly.
    const response = await fetch(`${API_URL}/api/admin/customers/${encodeURIComponent(customerId)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch customer');
    }
    return response.json();
  },

  // Subscription Management
  pauseSubscription: async (token, subscriptionId) => {
    const response = await fetch(`${API_URL}/api/admin/subscriptions/${subscriptionId}/pause`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to pause subscription');
    }
    return response.json();
  },

  resumeSubscription: async (token, subscriptionId) => {
    const response = await fetch(`${API_URL}/api/admin/subscriptions/${subscriptionId}/resume`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to resume subscription');
    }
    return response.json();
  },

  cancelSubscription: async (token, subscriptionId) => {
    const response = await fetch(`${API_URL}/api/admin/subscriptions/${subscriptionId}/cancel`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to cancel subscription');
    }
    return response.json();
  },

  cleanupInactiveSubscriptions: async (token) => {
    const response = await fetch(`${API_URL}/api/admin/subscriptions/cleanup-inactive`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to cleanup subscriptions');
    }
    return response.json();
  },

  cleanupCustomersWithoutSubscriptions: async (token) => {
    const response = await fetch(`${API_URL}/api/admin/subscriptions/cleanup-customers-without-subscriptions`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to cleanup customers');
    }
    return response.json();
  },

  // Reviews Management
  getReviews: async (token, status = null, page = 1, limit = 20) => {
    const params = new URLSearchParams({ token, page, limit });
    if (status) params.append('status', status);
    
    const response = await fetch(`${API_URL}/api/admin/reviews?${params}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch reviews');
    }
    return response.json();
  },

  createReview: async (token, reviewData) => {
    const response = await fetch(`${API_URL}/api/admin/reviews?token=${token}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(reviewData),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create review');
    }
    return response.json();
  },

  updateReview: async (token, reviewId, reviewData) => {
    const response = await fetch(
      `${API_URL}/api/admin/reviews/${reviewId}?token=${token}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reviewData),
      }
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update review');
    }
    return response.json();
  },

  deleteReview: async (token, reviewId) => {
    const response = await fetch(
      `${API_URL}/api/admin/reviews/${reviewId}?token=${token}`,
      { method: 'DELETE' }
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to delete review');
    }
    return response.json();
  },

  uploadReviewImage: async (token, file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(
      `${API_URL}/api/admin/reviews/upload-image?token=${token}`,
      {
        method: 'POST',
        body: formData,
      }
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to upload image');
    }
    return response.json();
  },

  // Partners Management
  getPartners: async (token, status = null, page = 1, limit = 20) => {
    const params = new URLSearchParams({ token, page, limit });
    if (status) params.append('status', status);
    
    const response = await fetch(`${API_URL}/api/admin/partners?${params}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch partners');
    }
    return response.json();
  },

  createPartner: async (token, partnerData) => {
    const response = await fetch(`${API_URL}/api/admin/partners?token=${token}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(partnerData),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create partner');
    }
    return response.json();
  },

  updatePartner: async (token, partnerId, partnerData) => {
    const response = await fetch(
      `${API_URL}/api/admin/partners/${partnerId}?token=${token}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(partnerData),
      }
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update partner');
    }
    return response.json();
  },

  deletePartner: async (token, partnerId) => {
    const response = await fetch(
      `${API_URL}/api/admin/partners/${partnerId}?token=${token}`,
      { method: 'DELETE' }
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to delete partner');
    }
    return response.json();
  },

  uploadPartnerImage: async (token, file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(
      `${API_URL}/api/admin/partners/upload-image?token=${token}`,
      {
        method: 'POST',
        body: formData,
      }
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to upload image');
    }
    return response.json();
  },

  // Admin Users Management
  getAdminUsers: async (token, status = null, page = 1, limit = 20) => {
    const params = new URLSearchParams({ token, page, limit });
    if (status) params.append('status', status);
    
    const response = await fetch(`${API_URL}/api/admin/users?${params}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch admin users');
    }
    return response.json();
  },

  createAdminUser: async (token, userData) => {
    const response = await fetch(`${API_URL}/api/admin/users?token=${token}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create admin user');
    }
    return response.json();
  },

  updateAdminUser: async (token, userId, userData) => {
    const response = await fetch(
      `${API_URL}/api/admin/users/${userId}?token=${token}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData),
      }
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update admin user');
    }
    return response.json();
  },

  updateAdminPassword: async (token, userId, newPassword) => {
    const response = await fetch(
      `${API_URL}/api/admin/users/${userId}/password?token=${token}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_password: newPassword }),
      }
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update password');
    }
    return response.json();
  },

  deleteAdminUser: async (token, userId) => {
    const response = await fetch(
      `${API_URL}/api/admin/users/${userId}?token=${token}`,
      { method: 'DELETE' }
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to delete admin user');
    }
    return response.json();
  },

  // Public Routes
  getActiveReviews: async () => {
    const response = await fetch(`${API_URL}/api/reviews/active`);
    if (!response.ok) {
      throw new Error('Failed to fetch reviews');
    }
    return response.json();
  },

  getActivePartners: async () => {
    const response = await fetch(`${API_URL}/api/partners/active`);
    if (!response.ok) {
      throw new Error('Failed to fetch partners');
    }
    return response.json();
  },
};
